import { DocumentResult } from "@/types/document/document";
import {
    DocumentInfo,
    DocumentInfoResponse,
    Controllers,
    DocumentField,
    DocumentContent,
    InteractiveDocumentContent,
    ProblemResult,
    IsServerErrorResult,
    IsBadRequestResult,
    IsNotFoundResult,
    IsForbiddenResult,
    QueryString,
    User
} from "@infoland/iprova-api";
import { OfficeOnlineParams } from "../../../types/OfficeOnlineParams";
import storeHelper from "@/store/store-helper";
import { CommentReply, EditCommentReply } from "@/types/commentReply";
import { DeletedComment } from "@/types/document/documentComment/deleteComment";
import { ActionTree, Dispatch } from "vuex";
import { RootState } from "@/store";
import { CommentType, AnchorType, DocumentVersionState, CommentState, DocumentCommentListMode } from "@/enums";
import { DocumentTypeParagraph, DocumentTypeParagraphsResult } from "@/types/document/documentTypeParagraph";
import { loadDocumentCommentsAsync, Comment } from "@/documentComments";
import Vue from "vue";
import { DocumentVersionInfo } from "@/types/document/documentVersionInfo";
import { CommentFilter } from "@/types/document/commentFilter";

//state type
export interface DocumentState {
    documentInfo: DocumentVersionInfo;
    documentComments: Comment[];
    commentFilters: CommentFilter[];
}

export interface ActionResult<S, E> {
    success: boolean;
    successData?: S;
    errorData?: E;
}

export function initialize(): DocumentState {
    return {
        documentInfo: null,
        documentComments: [],
        commentFilters: []
    };
}

//initial state
const state: DocumentState = initialize();

//getters for this store
const getters = {
    /**
     * The loaded document info
     * @param state The state to read from
     */
    documentInfo(state: DocumentState): DocumentVersionInfo {
        return state.documentInfo;
    },
    /**
     * The loaded document comments
     * @param state The state to read from
     */
    documentComments(state: DocumentState): Comment[] {
        return state.documentComments;
    },

    /**
     * The filters used in comments drawer panel
     * @param state The state to read from
     */
    commentFilters(state: DocumentState): CommentFilter[] {
        return state.commentFilters;
    }
};

// mutations for this store
const mutations = {
    /**
     * Set the document info
     * @param state The state to write to
     * @param documentInfo The document info
     */
    _documentInfo(state: DocumentState, documentInfo: DocumentVersionInfo): void {
        // undefined should become null
        if (!documentInfo) documentInfo = null;

        state.documentInfo = documentInfo;
    },
    /**
     * Set the document comments
     * @param state The state to write to
     * @param documentComments The document comments
     */
    _documentComments(state: DocumentState, documentComments: Comment[]): void {
        state.documentComments = documentComments;
    },

    /**
     * The comments filter used in  drawer panel
     * @param state The state to read from
     */
    commentFilters(state: DocumentState, filter: CommentFilter): void {
        const existingFilter = state.commentFilters.find(
            c => c.documentId == filter.documentId && c.documentVersionState === filter.documentVersionState
        );
        if (existingFilter) {
            return;
        }
        state.commentFilters.push(filter);
    },

    /**
     *
     * @param state The state to write to
     * @param deletedComment The comment that was deleted
     */

    _handleDeleteComment(state: DocumentState, deletedComment: DeletedComment): void {
        const deletedCommentIndex = findDocumentCommentIndex(state, deletedComment.comment_id);
        //The delete performed is soft, therefore we set the property in order to be able to access undelete
        Vue.set(state.documentComments[deletedCommentIndex], "deleted", true);
    },
    /**
     *
     * @param state The state to write to
     * @param editCommentReply The comment that was edited
     */
    _toggleEditCommentReply(state: DocumentState, editCommentReply: EditCommentReply): void {
        const commentIndex = findDocumentCommentIndex(state, editCommentReply.comment_id);
        if (commentIndex !== -1) {
            const commentReplyIndex = findDocumentCommentReplyIndex(state, editCommentReply.comment_reply_id, commentIndex);
            if (commentReplyIndex !== -1) {
                const commentReply = state.documentComments[commentIndex].comment_replies[commentReplyIndex];
                Vue.set(
                    state.documentComments[commentIndex].comment_replies[commentReplyIndex],
                    "editing",
                    "editing" in commentReply ? !commentReply.editing : true
                );
            }
        }
    }
};

const findDocumentCommentIndex = (state: DocumentState, commentId: number): number => {
    return state.documentComments.findIndex((documentComment: Comment) => documentComment.comment_id === commentId);
};

const findDocumentCommentReplyIndex = (state: DocumentState, commentReplyId: number, commentIndex: number): number => {
    return state.documentComments[commentIndex].comment_replies.findIndex(
        (commentReply: CommentReply) => commentReply.comment_reply_id === commentReplyId
    );
};

// actions for this store
const actions: ActionTree<DocumentState, RootState> = {
    /**
     * Loads the document info response
     * @param context The context of the action
     * @param documentId The id of the document
     */
    async loadDocumentAsync(context: { commit: (path: string, value: DocumentInfo) => void }, documentId: string): Promise<DocumentVersionInfo> {
        let documentResponse = null;
        // loads the document
        try {
            documentResponse = await Controllers.DocumentsController.getDocumentAsync(documentId, false, true);
        } catch (error) {
            documentResponse = {
                statusCode: (error as any).response.status ?? 500,
                documentInfo: null
            };
        }

        if (documentResponse.statusCode == 200) {
            context.commit("_documentInfo", documentResponse.documentInfo);
        }

        return documentResponse;
    },

    /**
     * Loads the document info response
     * @param context The context of the action
     * @param documentId The id of the document
     * @param state The state of the document
     */
    async loadDocumentByStateAsync(
        context: { commit: (path: string, value: DocumentVersionInfo) => void },
        document: { documentId: string; state: DocumentVersionState; includePrintForcedHeader: boolean }
    ): Promise<DocumentResult> {
        const result = await Controllers.GenericController.getAsync<DocumentVersionInfo | ProblemResult<string>>(
            `documents/${document.documentId}/${document.state}?include_print_forced_header=${document.includePrintForcedHeader}`
        );
        let documentResult: DocumentResult = {} as DocumentResult;
        if (!(IsServerErrorResult(result) || IsBadRequestResult(result) || IsForbiddenResult(result) || IsNotFoundResult(result))) {
            documentResult.problemResult = null;
            documentResult.documentInfo = result;
            context.commit("_documentInfo", result);
        } else {
            documentResult.problemResult = result as ProblemResult<string>;
            documentResult.documentInfo = null;
        }

        return documentResult;
    },

    /**
     * Loads the document info response
     * @param context The context of the action
     * @param documentId The id of the document
     * @param versionNumber The version number of the document
     */
    async loadDocumentByVersionAsync(
        context: { commit: (path: string, value: DocumentVersionInfo) => void },
        document: { documentId: string; versionNumber: number }
    ): Promise<DocumentResult> {
        const result = await Controllers.GenericController.getAsync<DocumentVersionInfo | ProblemResult<string>>(
            `documents/${document.documentId}/v${document.versionNumber}`
        );
        let documentResult: DocumentResult = {} as DocumentResult;
        if (!(IsServerErrorResult(result) || IsBadRequestResult(result) || IsForbiddenResult(result) || IsNotFoundResult(result))) {
            documentResult.problemResult = null;
            documentResult.documentInfo = result;
            context.commit("_documentInfo", result);
        } else {
            documentResult.problemResult = result as ProblemResult<string>;
            documentResult.documentInfo = null;
        }

        return documentResult;
    },

    /**
     * Gets the document content
     * @param documentId The id of the document
     * @param queryString The query parameters that help retrieve the content
     */
    async getDocumentContentAsync(
        { commit },
        document: { documentId: string; queryString: QueryString }
    ): Promise<DocumentContent | InteractiveDocumentContent> {
        // gets the document content
        const documentContent = await Controllers.DocumentsController.getDocumentContentAsync(document.documentId, document.queryString);
        return documentContent;
    },

    /**
     * Gets the document content by state
     * @param documentId The id of the document
     * @param state The state of the document
     * @param queryString The query parameters that help retrieve the content
     */
    async getDocumentContentByStateAsync(
        { commit },
        document: { documentId: string; state: DocumentVersionState; queryString: QueryString }
    ): Promise<DocumentContent | InteractiveDocumentContent | null> {
        // gets the document content
        let path: string = `documents/${document.documentId}/${document.state}/contents`;
        if (document.queryString) {
            let queryParameter: string = "";
            for (let key in document.queryString) queryParameter += `&${encodeURIComponent(key)}=${encodeURIComponent(document.queryString[key])}`;

            path += "?" + queryParameter.substring(1);
        }
        const documentContent = await Controllers.GenericController.getAsync<DocumentContent | InteractiveDocumentContent>(path);
        if (documentContent) return documentContent;
        else return null;
    },

    /**
     * Gets the published document content for iLuminus
     * @param documentID The id of the document
     * @param language The desired language of the document
     */
    async getIluminusDocumentContentAsync(
        { commit },
        document: { documentId: string; language: string }
    ): Promise<InteractiveDocumentContent | null> {
        // gets the iluminus document content in the desired language
        let path: string = `/documents/${document.documentId}/contents/${document.language}`;
        const documentContent = await Controllers.GenericController.getAsync<InteractiveDocumentContent>(path);
        if (documentContent) return documentContent;
        return null;
    },

    /**
     * Gets the document content by state
     * @param documentId The id of the document
     * @param version The version of the document
     * @param queryString The query parameters that help retrieve the content
     */
    async getDocumentContentByVersionAsync(
        { commit },
        document: { documentId: string; version: number; queryString: QueryString }
    ): Promise<DocumentContent | InteractiveDocumentContent | null> {
        // gets the document content
        let path: string = `documents/${document.documentId}/v${document.version}/contents`;
        if (document.queryString) {
            let queryParameter: string = "";
            for (let key in document.queryString) queryParameter += `&${encodeURIComponent(key)}=${encodeURIComponent(document.queryString[key])}`;

            path += "?" + queryParameter.substring(1);
        }
        const documentContent = await Controllers.GenericController.getAsync<DocumentContent | InteractiveDocumentContent>(path);
        if (documentContent) return documentContent;
        else return null;
    },

    /**
     * Loads the document info response
     * @param documentId The id of the document
     * @param documentVersion The version of the document
     */
    async getEmbeddedViewAsync({ commit }, embedded: { documentId: string; documentVersion: number }): Promise<OfficeOnlineParams> {
        // loads the document
        const response = await Controllers.GenericController.getAsync<OfficeOnlineParams>(
            "wopi/viewerparameters/embedview?document_id=" + embedded.documentId + "&version=" + embedded.documentVersion
        );
        return response;
    },

    /**
     * Get the office online edit parameters
     * @param documentId The id of the document
     * @param documentVersion The version of the document
     */
    async getOfficeOnlineFilleableFormParamsAsync(
        { commit },
        embedded: { documentId: string; documentVersion: number }
    ): Promise<OfficeOnlineParams> {
        // loads the document
        const response = await Controllers.GenericController.getAsync<OfficeOnlineParams>(
            "wopi/viewerparameters/fillableform?document_id=" + embedded.documentId + "&version=" + embedded.documentVersion
        );
        return response;
    },

    /**
     * Get the office online favicon
     * @param documentId The id of the document
     * @param documentVersion The version of the document
     */
    async getOfficeOnlineFaviconAsync({ commit }, embedded: { documentId: string; documentVersion: number }): Promise<string> {
        // loads the document
        const response = await Controllers.GenericController.getAsync<string>("wopi/favicon/" + embedded.documentId + "/" + embedded.documentVersion);
        return response;
    },

    /**
     * Save the comment
     * @param context The context of the action
     * @param documentId The id of the document
     * @param version The version of the document
     * @param comment The comment to be added
     */
    async addCommentAsync({ dispatch }, commentData: { documentId: string; version: number; comment: Comment }): Promise<ActionResult<void, string>> {
        const response = await Controllers.GenericController.postAsync<Object, void | ProblemResult<string>>(
            `documents/${commentData.documentId}/v${commentData.version}/comments/`,
            commentData.comment
        );
        return handleDocumentCommentMutationResult(response, dispatch, true);
    },

    /**
     * Save the comment
     * @param documentId The id of the document
     * @param comment_id The commentId  of the updated comment
     * @param commentText The comment content
     * @param anchorId The id of the anchor
     * @param anchorText The anchor text
     * @param anchorType The anchor type
     */
    async editCommentAsync(
        { dispatch },
        editComment: {
            documentId: string;
            documentVersion: number;
            comment_id: number;
            commentText: string;
            anchorId: string;
            anchorText: string;
            anchorType: AnchorType;
        }
    ): Promise<ActionResult<void, string>> {
        const response = await Controllers.GenericController.patchAsync<any, void>(`documents/comments/${editComment.comment_id}`, <Comment>{
            anchor_id: editComment.anchorId,
            anchor_text: editComment.anchorText,
            anchor_type: editComment.anchorType,
            text: editComment.commentText
        });
        return handleDocumentCommentMutationResult(response, dispatch, true);
    },

    /**
     * Get document comments by documentid and version
     * @param documentId The id of the document
     * @param documentVersionState The document version state
     */
    async getDocumentCommentsAsync(
        context: { commit },
        document: {
            documentId: string;
            documentState: DocumentVersionState;
        }
    ): Promise<Comment[]> {
        const urlPath = `documents/${document.documentId}/comments?include_documents=true&state=${document.documentState}`;
        const comments = await loadDocumentCommentsAsync(urlPath);
        if (comments) {
            context.commit("_documentComments", comments);
            return comments;
        }
    },

    /**
     * Get document comments by documentid and version
     * @param documentId The id of the document
     * @param version The version the document
     */
    async getDocumentCommentsByVersionAsync(context: { commit }, document: { documentId: string; version: number }): Promise<Comment[]> {
        const urlPath = `documents/${document.documentId}/v${document.version}/comments?include_documents=true`;
        const loadsCommentResponse = await loadDocumentCommentsAsync(urlPath);
        if (loadsCommentResponse) {
            return loadsCommentResponse;
        }
    },

    /**
     * Clear document comments from store
     */
    clearDocumentComments({ commit }): void {
        commit("_documentComments", []);
    },

    /** Delete a document comment
     *  @param deleteComment  The delete comment Object
     */

    async deleteCommentAsync({ commit, dispatch }, deleteComment: DeletedComment): Promise<ActionResult<void, string>> {
        const response = await Controllers.GenericController.deleteAsync<null, void | ProblemResult<string>>(
            `documents/comments/${deleteComment.comment_id}`
        );
        const result = handleDocumentCommentMutationResult(response, dispatch);
        if (result.success) {
            commit("_handleDeleteComment", deleteComment);
        }
        return result;
    },

    /** Undelete a document comment
     *  @param deleteComment  The delete comment Object
     */
    async undeleteCommentAsync({ dispatch }, deleteComment: DeletedComment): Promise<ActionResult<void, string>> {
        const response = await Controllers.GenericController.patchAsync<{ deleted: boolean }, void | ProblemResult<string>>(
            `documents/comments/${deleteComment.comment_id}`,
            {
                deleted: deleteComment.delete_value
            }
        );
        return handleDocumentCommentMutationResult(response, dispatch, true);
    },

    /** Add reply to a document comment
     *  @param commentId  The id of the comment to add the Reply to
     *  @param commentReply The CommentReply Object
     */
    async addCommentReplyAsync({ dispatch }, reply: { comment_id: number; commentReply: CommentReply }): Promise<ActionResult<void, string>> {
        const response = await Controllers.GenericController.postAsync<CommentReply, number | ProblemResult<string>>(
            `documents/comments/${reply.comment_id}/replies`,
            reply.commentReply
        );
        return handleDocumentCommentMutationResult(response, dispatch, true);
    },

    /** Edit reply to a document comment
     *  @param commentId  The id of the comment to add the Reply to
     *  @param commentReply The CommentReply Object
     */
    async editCommentReplyAsync({ dispatch }, reply: { comment_id: number; commentReply: CommentReply }): Promise<ActionResult<void, string>> {
        const response = await Controllers.GenericController.patchAsync<CommentReply, void | ProblemResult<string>>(
            `documents/comments/${reply.comment_id}/replies/${reply.commentReply.comment_reply_id}`,
            reply.commentReply
        );
        return handleDocumentCommentMutationResult(response, dispatch, true);
    },

    /** Add reply to a document comment
     *  @param commentId  The id of the comment to add the Reply to
     *  @param comment_reply_id The Comment Reply Id
     */
    toggleCommentReply({ commit }, editCommentReply: EditCommentReply): void {
        commit("_toggleEditCommentReply", editCommentReply);
    },

    /** update status of a document comment
     *  @param updateCommentState Obj with The comment Id The new comment state
     */
    async updateDocumentCommentState(
        { dispatch },
        updateCommentStateData: { commentId: number; commentState: CommentState }
    ): Promise<ActionResult<void, string>> {
        const response = await Controllers.GenericController.patchAsync<{ state: CommentState }, void | ProblemResult<string>>(
            `documents/comments/${updateCommentStateData.commentId}`,
            {
                state: updateCommentStateData.commentState
            }
        );
        return handleDocumentCommentMutationResult(response, dispatch, true);
    },
    /**
     * Gets the document type paragraphs
     * @param documentTypeId The id of the document
     */
    async getDocumentTypeParagraphsAsync({ commit }, documentTypeId): Promise<DocumentTypeParagraphsResult> {
        const getParagraphsResponse = await Controllers.GenericController.getAsync<DocumentTypeParagraph[] | ProblemResult<string>>(
            `/documents/document_types/${documentTypeId}/paragraphs`
        );
        if (IsNotFoundResult(getParagraphsResponse)) {
            // When document type not found then empty array is returned
            return { documentTypeExists: false, documentTypeParagraphs: [] };
        } else {
            return { documentTypeExists: true, documentTypeParagraphs: getParagraphsResponse };
        }
    }
};

function handleDocumentCommentMutationResult(
    result: void | number | ProblemResult<string>,
    dispatch: Dispatch,
    reloadDocumentComments: boolean = false
): ActionResult<void, string> {
    if (IsServerErrorResult(result) || IsBadRequestResult(result) || IsNotFoundResult(result) || IsForbiddenResult(result)) {
        return { success: false };
    } else {
        if (reloadDocumentComments) {
            dispatch("getDocumentCommentsAsync", {
                documentId: storeHelper.documentStore.documentInfo.document_id,
                documentState: storeHelper.documentStore.documentInfo.state
            });
        }
        return { success: true };
    }
}

// export the namespace
export const namespace = "document";

// export the store 'module'
export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
